import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import type { HelpArticle } from "../lib/types";
import { fetchRevisions, restoreRevision, fetchArticleById } from "../lib/admin-data";
import { renderMarkdown } from "../lib/markdown";
import { SEO } from "../components/SEO";

interface RevisionRow {
  id: number;
  article_id: string;
  slug: string;
  title: string;
  description: string;
  content_markdown: string;
  parent_slug: string | null;
  nav_order: number;
  page_type: string;
  is_published: boolean;
  archived_at: string | null;
  changed_by: string | null;
  changed_at: string;
}

export function AdminRevisionsPage() {
  const { id } = useParams();
  const [article, setArticle] = useState<HelpArticle | null>(null);
  const [revisions, setRevisions] = useState<RevisionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRev, setSelectedRev] = useState<RevisionRow | null>(null);

  useEffect(() => {
    if (id) load(id);
  }, [id]);

  async function load(articleId: string) {
    setLoading(true);
    setError(null);
    try {
      const art = await fetchArticleById(articleId);
      setArticle(art);
      const revs = await fetchRevisions(articleId);
      setRevisions(revs as unknown as RevisionRow[]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unknown error");
    }
    setLoading(false);
  }

  async function handleRestore(revId: number) {
    if (!id || !confirm("Restore this revision? The current article will be replaced with the revision's content.")) return;
    try {
      await restoreRevision(id, revId);
      await load(id);
      setSelectedRev(null);
    } catch (e) {
      alert(e instanceof Error ? e.message : "Failed to restore");
    }
  }

  if (loading) {
    return (
      <div className="loading-state">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <>
      <SEO
        title={`Revisions: ${article?.title || "Unknown"} — Admin`}
        description="View and restore article revisions."
        canonical="https://help.trustreq.ca/admin/help"
      />
      <div className="admin-page">
        <div className="admin-header">
          <div className="admin-header-left">
            <Link to="/admin/help" className="btn btn-secondary">← Back</Link>
            <h1 className="admin-title">Revisions: {article?.title}</h1>
          </div>
          {article && (
            <div className="admin-header-right">
              <Link to={`/admin/help/edit/${article.id}`} className="btn btn-secondary">Edit Article</Link>
            </div>
          )}
        </div>

        {error && <div className="admin-error">{error}</div>}

        {revisions.length === 0 ? (
          <div className="admin-empty">No revisions recorded for this article.</div>
        ) : (
          <div className="revisions-layout">
            <div className="revisions-list">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Changed By</th>
                    <th>Slug</th>
                    <th>Published</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {revisions.map((r) => (
                    <tr key={r.id} className={selectedRev?.id === r.id ? "row-selected" : ""}>
                      <td className="cell-date">
                        <button className="rev-link" onClick={() => setSelectedRev(r)}>
                          {new Date(r.changed_at).toLocaleString()}
                        </button>
                      </td>
                      <td className="cell-user">{r.changed_by ? "User" : "System"}</td>
                      <td className="cell-slug">{r.slug}</td>
                      <td className="cell-status">
                        {r.is_published ? "Yes" : "No"}
                      </td>
                      <td className="cell-actions">
                        <button onClick={() => handleRestore(r.id)} className="btn btn-sm btn-primary">Restore</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {selectedRev && (
              <div className="revision-preview">
                <h3 className="revision-preview-title">
                  Preview: {selectedRev.title}
                </h3>
                <div className="revision-meta">
                  <span>Slug: {selectedRev.slug}</span>
                  <span>Type: {selectedRev.page_type}</span>
                  <span>Order: {selectedRev.nav_order}</span>
                  <span>Published: {selectedRev.is_published ? "Yes" : "No"}</span>
                </div>
                <div
                  className="article-body markdown-body"
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(selectedRev.content_markdown).html }}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
